import React from 'react';
import Input from '../common/Input';
import RichTextEditor from '../forms/RichTextEditor';
import FileUploader from '../forms/FileUploader';

// Simple ID generator for internal use
const generateSimpleId = () => Math.random().toString(36).substr(2, 9);

function ItineraryDayInput({ dayNumber, itinerary, onChange, onImageUpload }) {
  const handleInputChange = (field, value) => {
    onChange(dayNumber, { ...itinerary, [field]: value });
  };

  const handleImageUpload = (urls) => {
    // This component only handles a single image for now per day, assuming an array of URLs for gallery_images
    // If you plan for multiple, adjust here.
    const newImages = Array.isArray(urls) ? urls : (urls ? [urls] : []);
    onImageUpload(dayNumber, newImages);
  };

  return (
    <div className="border border-blue-200 p-4 rounded-lg bg-blue-50 mb-6">
      <h3 className="text-xl font-semibold text-blue-800 mb-4">Day {dayNumber} Details</h3>
      <Input
        label={`Day ${dayNumber} Title`}
        id={`day-${dayNumber}-title`}
        value={itinerary.title || ''}
        onChange={(e) => handleInputChange('title', e.target.value)}
        placeholder={`e.g., Arrival & Sightseeing in Day ${dayNumber}`}
      />
      <RichTextEditor
        label={`Day ${dayNumber} Description`}
        value={itinerary.description || ''}
        onChange={(value) => handleInputChange('description', value)}
      />
      <Input
        label="Activities (comma-separated)"
        id={`day-${dayNumber}-activities`}
        value={itinerary.activities ? itinerary.activities.join(', ') : ''}
        onChange={(e) => handleInputChange('activities', e.target.value.split(',').map(s => s.trim()))}
        placeholder="e.g., Trekking, Boating"
      />
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-semibold mb-2">Meals Included</label>
        <div className="flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
              checked={itinerary.meals_included?.breakfast || false}
              onChange={(e) => handleInputChange('meals_included', { ...itinerary.meals_included, breakfast: e.target.checked })}
            />
            <span className="ml-2 text-gray-700">Breakfast</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
              checked={itinerary.meals_included?.lunch || false}
              onChange={(e) => handleInputChange('meals_included', { ...itinerary.meals_included, lunch: e.target.checked })}
            />
            <span className="ml-2 text-gray-700">Lunch</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
              checked={itinerary.meals_included?.dinner || false}
              onChange={(e) => handleInputChange('meals_included', { ...itinerary.meals_included, dinner: e.target.checked })}
            />
            <span className="ml-2 text-gray-700">Dinner</span>
          </label>
        </div>
      </div>
      <Input
        label="Accommodation Type"
        id={`day-${dayNumber}-accommodation`}
        value={itinerary.accommodation_type || ''}
        onChange={(e) => handleInputChange('accommodation_type', e.target.value)}
        placeholder="e.g., 3-star Hotel, Camp"
      />
      <Input
        label="Transportation Details"
        id={`day-${dayNumber}-transportation`}
        value={itinerary.transportation_details || ''}
        onChange={(e) => handleInputChange('transportation_details', e.target.value)}
        placeholder="e.g., Private AC Car"
      />
      <Input
        label="Places Covered (comma-separated)"
        id={`day-${dayNumber}-places-covered`}
        value={itinerary.places_covered ? itinerary.places_covered.join(', ') : ''}
        onChange={(e) => handleInputChange('places_covered', e.target.value.split(',').map(s => s.trim()))}
        placeholder="e.g., Lake, Temple, Market"
      />
      <FileUploader
        label={`Day ${dayNumber} Images`}
        multiple={true}
        initialFiles={itinerary.images?.map(url => ({ id: generateSimpleId(), url, name: url.split('/').pop() })) || []}
        onUploadComplete={(urls) => handleImageUpload(urls)}
      />
    </div>
  );
}

export default ItineraryDayInput;